import React, { useState, useEffect } from "react";
import { send } from "emailjs-com";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

const initialState = { name: "", email: "", message: "" };
const SUPPORT_EMAIL = "coloryouapp@gmail.com";

export const Contact = () => {
  const [{ name, email, message }, setState] = useState(initialState);
  const [topic, setTopic] = useState("support");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [hp, setHp] = useState(""); // honeypot
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const q = new URLSearchParams(location.search).get("topic");
    if (q && ["support", "collab", "press", "other"].includes(q)) setTopic(q);
  }, [location.search]);

  const topics = [
    { value: "support", label: t("topic_support") },
    { value: "collab", label: t("topic_collab") },
    { value: "press", label: t("topic_press") },
    { value: "other", label: t("topic_other") },
  ];
  const topicLabel =
    topics.find((o) => o.value === topic)?.label || "Soporte de la app";
  const subject = `ColorYou: ${topicLabel}`;
  const mailtoHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(
    `Describe tu consulta y, si aplica, los pasos para reproducirla.

Datos útiles:
• Versión de la app
• Dispositivo y versión del sistema
• Tipo de compra (pase 1 semana o acceso indefinido)
• Número de pedido (Apple o Google Play)
`
  )}`;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (hp.trim()) {
      setIsFormSubmitted(true);
      return;
    }

    setIsSubmitting(true);
    const payload = {
      from_name: name,
      from_mail: email,
      reply_to: email,
      message,
      topic,
      subject,
      page_url: window.location.href,
      user_agent: navigator.userAgent,
    };

    try {
      await send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        payload,
        process.env.REACT_APP_EMAILJS_USER_ID
      );
      setIsFormSubmitted(true);
      setState(initialState);
    } catch (err) {
      console.error("EmailJS FAILED", err);
      setErrorMsg(t("send_error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div className="container">
        <div className="col-md-8">
          <div className="row">
            <div className="section-title">
              <h2>{t("contact_support_title")}</h2>
              {!isFormSubmitted && (
                <>
                  <p>{t("FillOut")}</p>
                  <p style={{ marginTop: 8 }}>
                    <strong>{t("support_label")}:</strong>{" "}
                    <a href={mailtoHref}>{SUPPORT_EMAIL}</a>{" "}
                    <span className="text-muted">{t("sla_text")}</span>
                  </p>
                </>
              )}
            </div>

            {!isFormSubmitted ? (
              <form name="sentMessage" onSubmit={handleSubmit}>
                {/* Motivo */}
                <div className="row" style={{ marginBottom: 12 }}>
                  <div className="col-md-12">
                    <label htmlFor="topic" className="form-label">
                      {t("Topic")}
                    </label>
                    <select
                      id="topic"
                      className="form-control"
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                    >
                      {topics.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>

                    {topic === "support" && (
                      <p className="help-block">{t("hint_support")}</p>
                    )}
                    {topic === "collab" && (
                      <p className="help-block">{t("hint_collab")}</p>
                    )}
                  </div>
                </div>

                {/* Nombre y Email */}
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <input
                        type="text"
                        id="name"
                        name="name"
                        className="form-control"
                        placeholder={t("Name")}
                        required
                        onChange={handleChange}
                        value={name}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className="form-control"
                        placeholder={t("Email")}
                        required
                        onChange={handleChange}
                        value={email}
                      />
                    </div>
                  </div>
                </div>

                {/* Mensaje */}
                <div className="form-group">
                  <textarea
                    name="message"
                    id="message"
                    className="form-control"
                    rows="4"
                    placeholder={t("Message")}
                    required
                    onChange={handleChange}
                    value={message}
                    style={{ fontSize: "16px" }}
                  />
                </div>

                {/* Honeypot oculto */}
                <div
                  style={{ position: "absolute", left: "-9999px" }}
                  aria-hidden="true"
                >
                  <label>
                    No rellenar:
                    <input
                      type="text"
                      name="company"
                      tabIndex={-1}
                      autoComplete="off"
                      value={hp}
                      onChange={(e) => setHp(e.target.value)}
                    />
                  </label>
                </div>

                {errorMsg && (
                  <p className="text-danger" style={{ marginTop: 8 }}>
                    {errorMsg} <a href={mailtoHref}>{t("or_email_us")}</a>.
                  </p>
                )}

                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <button
                    type="submit"
                    className="btn btn-custom btn-lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? t("Sending") : t("SendMessage")}
                  </button>
                </div>
              </form>
            ) : (
              <div>
                <h3 className="head-text">{t("Thanks")}</h3>
                <p>
                  <a href={mailtoHref}>{t("contact_anytime")}</a>.
                </p>
              </div>
            )}

            {/* Ayuda rápida con bullets y enlaces */}
            <div
              className="support-quick"
              style={{ marginTop: 24, color: "white" }}
            >
              <h4 style={{ color: "white" }}>{t("quick_help")}</h4>
              <ul className="support-list">
                <li>
                  <span className="tag">{t("general")}</span>
                  {t("restore_purchases")}
                </li>

                {/* Opcional para revisión
                <li>
                  <span className="tag">{t("general")}</span>
                  {t("restore_alt")}
                </li>
                */}

                <li>
                  <span className="tag ios">iOS</span>
                  <a
                    href="https://reportaproblem.apple.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("refunds_ios_link")}
                  </a>
                </li>
                <li>
                  <span className="tag android">Android</span>
                  <a
                    href="https://play.google.com/store/account/orderhistory"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("refunds_android_link")}
                  </a>
                </li>

                <li>
                  <span className="tag">{t("general")}</span>
                  {t("platform_note")}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
